import IconErrorSolid from '@apollo/icons/default/IconErrorSolid.svg';
import IconSuccessSolid from '@apollo/icons/default/IconSuccessSolid.svg';
import IconTime from '@apollo/icons/default/IconTime.svg';
import IconWarningSolid from '@apollo/icons/default/IconWarningSolid.svg';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { isValidUrl } from 'src/lib/isValidUrl';

/**
 * Possible states for endpoint reachability status
 */
export type EndpointStatusValue =
  | 'reachable'
  | 'unreachable'
  | 'invalid'
  | 'no-endpoint'
  | 'checking';

interface EndpointStatusProps {
  /** The endpoint URL to check. If null/undefined, shows "No endpoint detected" */
  url?: string | null;
  /** Gate the check behind viewport visibility */
  inView?: boolean;
}

// Module-scope cache to avoid re-checking the same URLs
const reachabilityCache = new Map<string, boolean>();

/**
 * Displays the status of a GraphQL endpoint with appropriate icons and badges.
 * Shows reachable for valid URLs, no-endpoint for missing URLs.
 */
export const EndpointStatus = ({
  url,
  inView = false,
}: EndpointStatusProps) => {
  const [isReachable, setIsReachable] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  // Test reachability when URL changes AND component is visible
  useEffect(() => {
    if (!url || !isValidUrl(url) || !inView) {
      setIsReachable(null);
      return;
    }

    const normalizedUrl = url.trim();

    // Check cache first
    if (reachabilityCache.has(normalizedUrl)) {
      const cachedResult = reachabilityCache.get(normalizedUrl);
      if (cachedResult !== undefined) {
        setIsReachable(cachedResult);
        return;
      }
    }

    const testEndpoint = async () => {
      setIsChecking(true);
      try {
        await fetch(normalizedUrl, {
          method: 'HEAD',
          mode: 'no-cors', // Avoid CORS issues for basic test
        });
        setIsReachable(true);
        reachabilityCache.set(normalizedUrl, true);
      } catch (error) {
        setIsReachable(false);
        reachabilityCache.set(normalizedUrl, false);
      } finally {
        setIsChecking(false);
      }
    };

    const timeoutId = setTimeout(testEndpoint, 1000);
    return () => clearTimeout(timeoutId);
  }, [url, inView]);

  const getDisplayStatus = (): EndpointStatusValue => {
    if (!url) return 'no-endpoint';
    if (!isValidUrl(url)) return 'invalid';
    if (!inView) return 'no-endpoint'; // Show awaiting until visible
    if (isChecking) return 'checking';
    if (isReachable === null) return 'checking';
    if (isReachable) return 'reachable';
    return 'unreachable';
  };

  const getStatusContent = () => {
    const displayStatus = getDisplayStatus();
    switch (displayStatus) {
      case 'reachable':
        return {
          icon: <IconSuccessSolid className="icon-success size-4" />,
          label: 'Reachable',
          tooltip: 'The URL is valid and reachable.',
          textClass: 'text-success',
        };
      case 'invalid':
        return {
          icon: <IconErrorSolid className="size-4" />,
          label: 'Invalid URL',
          tooltip: 'Please enter a valid URL, like https://example.com.',
          textClass: 'text-error',
        };
      case 'unreachable':
        return {
          icon: <IconWarningSolid className="size-4" />,
          label: 'Unreachable',
          tooltip: "This URL can't be reached. Please check and try again.",
          textClass: 'text-warning',
        };
      case 'checking':
        return {
          icon: <IconTime className="size-4" />,
          label: 'Checking...',
          tooltip: 'Testing endpoint reachability...',
        };
      case 'no-endpoint':
        return {
          icon: <IconTime className="size-4" />,
          label: 'Awaiting',
          tooltip: 'No endpoint URL has been configured for this variant.',
        };
      default:
        return {
          icon: <IconWarningSolid className="size-4" />,
          label: 'Unknown',
          tooltip: 'Unknown endpoint status.',
        };
    }
  };

  const { icon, label, tooltip, textClass } = getStatusContent();

  return (
    <div className="text-sm">
      <Tooltip label={tooltip}>
        <div className="flex items-center gap-2">
          <div className="flex size-4 items-center justify-center">{icon}</div>
          <span
            className={classnames({
              'text-success': textClass === 'text-success',
              'text-error': textClass === 'text-error',
              'text-warning': textClass === 'text-warning',
            })}
          >
            {label}
          </span>
        </div>
      </Tooltip>
    </div>
  );
};
